import { FunctionComponent, useEffect, useState } from 'react'
import { css } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'

import TileSelect, { TileOption } from '@/components/TileSelect'
import CustomTipModal from '@/components/TipSelect/CustomTipModal'
import { calcTipAmount } from '@/libs/helpers/utils'

const tipTileStyles = (theme: Theme): string => css`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;

  @media (max-width: ${theme.breakpoints.SM}) {
    grid-template-columns: 1fr;
  }
`

interface TipSelectProps {
  /** Optional onChange callback to hook into tip amount change event */
  onChange?: (tipAmount: string) => void
  totalAfterTax: string
  isCartLoading: boolean
}

const TipSelect: FunctionComponent<TipSelectProps> = ({
  onChange,
  totalAfterTax,
  isCartLoading,
}) => {
  // STATES
  const [openCustomTip, setOpenCustomTip] = useState<boolean>(false)
  const [customTipAmount, setCustomTipAmount] = useState<string>('')
  const [selectedTip, setSelectedTip] = useState<string>('20')
  const theme = useTheme()

  // HOOKS
  const { t } = useTranslation()

  // CONSTS
  const tipDenominations = ['10', '15', '20', '25', 'custom']
  const isCustomTip = selectedTip === 'custom'

  // EFFECTS
  useEffect(() => {
    if (onChange && !isCustomTip) {
      const newTipAmount = calcTipAmount(totalAfterTax, selectedTip)
      if (newTipAmount) {
        onChange(newTipAmount)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAfterTax, selectedTip, isCustomTip])

  // HANDLERS
  const handleCustomTipChange = (newTipAmount: string): void => {
    setCustomTipAmount(newTipAmount)
    if (onChange) {
      onChange(newTipAmount)
    }
  }

  const tipOptions = tipDenominations.map((tipDenomination: string) => {
    const renderTipAmount = (): TileOption['subtitle'] => {
      const isCurrentTipCustom = tipDenomination === 'custom'
      // custom tip amount is only shown if it's the current selection
      if (isCurrentTipCustom && !isCustomTip) {
        return { value: null }
      }

      const tipEstimateValue = isCurrentTipCustom
        ? customTipAmount
        : calcTipAmount(totalAfterTax, tipDenomination)

      return {
        value: t('checkout.payment.tip_amount', {
          value: tipEstimateValue,
        }),
        align: 'center',
        m: '4px 0 0 0',
      }
    }

    const option: TileOption = {
      value: tipDenomination,
      label: t('checkout.payment.tip_select', { context: tipDenomination }),
      subtitle: renderTipAmount(),
    }

    if (tipDenomination === 'custom') {
      option.onClick = (): void => {
        setOpenCustomTip(true)
      }
    }

    return option
  })

  const handleTipTileChange = (tipDenomination: string): void => {
    setSelectedTip(tipDenomination)
  }

  return (
    <>
      <TileSelect
        value={selectedTip}
        options={tipOptions}
        name="tip-select"
        onChange={handleTipTileChange}
        className={tipTileStyles(theme)}
      />
      <CustomTipModal
        onClose={(): void => setOpenCustomTip(false)}
        onChange={handleCustomTipChange}
        selectedTip={selectedTip}
        open={openCustomTip}
        totalAfterTax={totalAfterTax}
        isCartLoading={isCartLoading}
      />
    </>
  )
}

export default TipSelect
